import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatEUR, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Loader2, CheckCircle2, XCircle, Nfc } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export default function TerminalPayDialog({ invoice, onClose, onPaid }) {
  const { t } = useLang();
  const [readerId, setReaderId] = useState("");
  const [pi, setPi] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | waiting | paid | failed
  const timer = useRef(null);
  const { data } = useQuery({ queryKey: ["terminal-readers"], queryFn: () => api.get("/terminal/readers").then((r) => r.data), enabled: !!invoice });
  const readers = data?.readers || [];
  const online = readers.filter((r) => r.status === "online");

  useEffect(() => { if (!readerId && online.length) setReaderId(online[0].id); }, [online, readerId]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (!invoice) { setPi(null); setPhase("idle"); } }, [invoice]);

  const poll = async (id) => {
    try {
      const { data: s } = await api.get(`/terminal/payments/${id}/status`);
      if (s.payment_status === "paid") { setPhase("paid"); toast.success(t("terminalPaid")); onPaid?.(); return; }
      if (["failed", "canceled", "cancelled"].includes(s.payment_status)) { setPhase("failed"); return; }
    } catch (_) { /* keep polling */ }
    timer.current = setTimeout(() => poll(id), 1500);
  };

  const start = async () => {
    try {
      const { data: r } = await api.post(`/terminal/invoices/${invoice.id}/charge`, { reader_id: readerId });
      setPi(r.payment_intent_id); setPhase("waiting"); poll(r.payment_intent_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async () => {
    clearTimeout(timer.current);
    if (pi) await api.post(`/terminal/payments/${pi}/cancel`).catch(() => {});
    setPi(null); setPhase("idle");
  };

  const simulate = async () => {
    try { await api.post(`/terminal/readers/${readerId}/simulate-tap`); toast.success(t("terminalSimulated")); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(v) => { if (!v) { cancel(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="terminal-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> {t("terminalTitle")}</DialogTitle>
          <DialogDescription>{t("terminalHint")}</DialogDescription>
        </DialogHeader>
        {invoice && (
          <div className="space-y-4">
            <div className="p-4 rounded-md border border-border bg-muted/40 text-center">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{invoice.invoice_number} · {invoice.customer_name || t("walkIn")}</div>
              <div className="font-display text-4xl font-black mt-1" data-testid="terminal-amount">{formatEUR(invoice.total)}</div>
            </div>
            {phase === "idle" && (
              <div className="space-y-1.5">
                <Select value={readerId} onValueChange={setReaderId}>
                  <SelectTrigger data-testid="terminal-reader-select"><SelectValue placeholder={t("terminalPickReader")} /></SelectTrigger>
                  <SelectContent>
                    {readers.map((r) => <SelectItem key={r.id} value={r.id} disabled={r.status !== "online"}>{r.label} · {r.status === "online" ? t("terminalOnline") : t("terminalOffline")}</SelectItem>)}
                  </SelectContent>
                </Select>
                {readers.length === 0 && <div className="text-xs text-muted-foreground" data-testid="terminal-no-readers">{t("terminalNoReaders")}</div>}
              </div>
            )}
            {phase === "waiting" && (
              <div className="flex flex-col items-center gap-3 py-4" data-testid="terminal-waiting">
                <div className="relative"><Nfc className="h-14 w-14 text-primary animate-pulse" /><Loader2 className="h-5 w-5 absolute -right-2 -top-2 animate-spin text-muted-foreground" /></div>
                <div className="font-semibold">{t("terminalWaiting")}</div>
                <div className="text-xs text-muted-foreground text-center">{t("terminalWaitingHint")}</div>
                {data?.test_mode && <Button size="sm" variant="outline" className="rounded-full" onClick={simulate} data-testid="terminal-simulate-btn">{t("terminalSimulate")}</Button>}
              </div>
            )}
            {phase === "paid" && <div className="flex flex-col items-center gap-2 py-4 text-emerald-600" data-testid="terminal-paid"><CheckCircle2 className="h-14 w-14" /><div className="font-display text-xl font-bold">{t("terminalPaid")}</div></div>}
            {phase === "failed" && <div className="flex flex-col items-center gap-2 py-4 text-rose-600" data-testid="terminal-failed"><XCircle className="h-14 w-14" /><div className="font-semibold">{t("terminalFailed")}</div></div>}
          </div>
        )}
        <DialogFooter>
          {phase === "waiting" && <Button variant="ghost" onClick={cancel} data-testid="terminal-cancel-btn">{t("cancel")}</Button>}
          {phase === "idle" && <Button className="rounded-full bg-primary hover:bg-primary/90" disabled={!readerId} onClick={start} data-testid="terminal-start-btn"><Nfc className="h-4 w-4 mr-2" />{t("terminalStart")}</Button>}
          {phase === "failed" && <Button className="rounded-full" onClick={() => setPhase("idle")} data-testid="terminal-retry-btn">{t("retry")}</Button>}
          {phase === "paid" && <Button className="rounded-full" onClick={onClose} data-testid="terminal-done-btn">{t("close")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
